import { Check, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";
import { BuyButton } from "./BuyButton";
import { useShop } from "@/lib/shop";

const Option = ({ testId, label, title, to, price, note, points, featured, children }) => (
  <div data-testid={testId}
    className={`relative flex h-full flex-col border p-8 md:p-10 transition-colors duration-300 ${featured ? "border-cta bg-[#0a0f1f]" : "border-line bg-[#0b0b0b] hover:border-zinc-600"}`}>
    {featured && <span className="eyebrow absolute -top-3 left-8 bg-cta px-3 py-1 !text-white">Recommended</span>}
    <p className="eyebrow">{label}</p>
    <h3 className="font-display mt-4 text-2xl font-medium tracking-tight text-white">
      <Link to={to} data-testid={`${testId}-link`} className="group inline-flex items-center gap-2 hover:text-zinc-200">
        {title}<ArrowUpRight size={18} className="text-zinc-400 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-white" />
      </Link>
    </h3>
    <p className="font-display mt-8 text-5xl font-semibold tracking-tight text-white">{price}</p>
    <p className="mt-2 text-sm text-zinc-400">{note}</p>
    <ul className="mt-8 flex-1 space-y-3">
      {points.map((p) => (
        <li key={p} className="flex gap-3 text-sm text-zinc-300"><Check size={16} className="mt-0.5 shrink-0 text-white" />{p}</li>
      ))}
    </ul>
    <div className="mt-10">{children}</div>
    <Link to={to} className="link-u mt-4 self-center text-sm text-zinc-400 hover:text-white">See full details</Link>
  </div>
);

export const PricingOptions = () => {
  const { products, money } = useShop();
  const { ebook, bundle } = products;
  return (
    <div className="grid gap-6 lg:grid-cols-3 lg:items-stretch">
      <Reveal className="lg:mt-10">
        <Option testId="price-option-ebook" label="Read" title="The eBook" to="/products/ebook" price={money(ebook.price, ebook.currency)} note="One-time payment"
          points={["6 chapters, 25 lessons", "Instant download", "7-day money-back guarantee"]}>
          <BuyButton role="ebook" className="w-full !bg-transparent border border-white/30 hover:!bg-white/5">Get the eBook</BuyButton>
        </Option>
      </Reveal>
      <Reveal delay={0.08}>
        <Option testId="price-option-bundle" featured label="Read + apply" title="The Bundle" to="/products/bundle" price={money(bundle.price, bundle.currency)} note="One-time payment, eBook + Workbook"
          points={["The eBook: 6 chapters, 25 lessons", "The 180-page Workbook, 11 worksheets", "Learn and apply in the same week", "7-day money-back guarantee"]}>
          <BuyButton role="bundle" className="w-full">Get the Bundle</BuyButton>
        </Option>
      </Reveal>
      <Reveal delay={0.16} className="lg:mt-10">
        <Option testId="price-option-installment" label="Read + apply" title="Bundle in 2 instalments" to="/products/bundle" price={`2 × ${money(bundle.installment, bundle.currency)}`} note="Same Bundle, split into two payments"
          points={["Everything in the Bundle", "Start today, pay half now", "7-day money-back guarantee"]}>
          <BuyButton role="bundle" installment className="w-full !bg-transparent border border-white/30 hover:!bg-white/5">Pay in 2 instalments</BuyButton>
        </Option>
      </Reveal>
    </div>
  );
};
